import React, { useState, useEffect, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import { Toast, Spinner, Popover, OverlayTrigger } from "react-bootstrap";
import axios from "axios";
import { IoMdCheckmark } from "react-icons/io";
import { FaPencilAlt, FaRegQuestionCircle, FaEye, FaTimes } from "react-icons/fa";
import RefuseReservationModal from "../../components/refuse-reservation-modal/refuse-reservation-modal.component";
import EditReservationModal from "../../components/edit-reservation-modal/edit-reservation-modal.component";
import ConfirmationModal from "../../components/confirmation-modal/confirmation-modal.component";
import "./notification.styles.scss";

import { useDispatch } from "react-redux";

import API_ROUTE from "../../utils/apiRoute";
import useModal from "../../utils/useModal";
import { GlobalContext } from "../../GlobalState";

const NotificationPage = ({ isSystemMasterManager }) => {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [reservationData, setReservationData] = useState([]);
  const [arrayOfNotifications, setArrayOfNotifications] = useState([]);
  const { selectedSchoolCode } = useContext(GlobalContext);

  const inputRef = useState(React.createRef())[0];

  const [clickedReservData, setClickedReservData] = useState({});

  const { isShowing: isShowingRefuseReservationModal, toggle: toggleRefuseReservationModal } = useModal();
  const { isShowing: isShowingEditReservationModal, toggle: toggleEditReservationModal } = useModal();
  const { isShowing: isShowingConfirmationModal, toggle: toggleConfirmationModal } = useModal();

  const [filterType, setFilterType] = useState("requester");
  const [selectedSort, setSelectedSort] = useState("recents");
  const [inputFilterValue, setInputFilterValue] = useState("");
  const [reservationType, setReservationType] = useState("em análise");
  const [reservationOrigin, setReservationOrigin] = useState("RESERVAS");
  const [dateType, setDateType] = useState("requestDate");
  const [isAllFromAnalysis, setIsAllFromAnalysis] = useState(true);
  const [shouldFetch, setShouldFetch] = useState(true);

  const history = useHistory();

  const getFormattedDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  const today = new Date();
  const eightDaysAgo = new Date();
  eightDaysAgo.setDate(today.getDate() - 8);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const goToTimetable = useCallback((reservationData) => {
    const dateObj = new Date(reservationData.timeStart);
    const val = dateObj;

    let day = val.getDay().toString();
    if (val.getDay().toString() === "0") {
      day = "7";
    }
    const strDay = `${val.getFullYear().toString()}-${(
      val.getMonth() + 1
    ).toString()}-${val.getDate().toString()}-${day}`;

    dispatch({ type: "SET_DATE", payload: [strDay] });

    history.push(
      `/floor/${reservationData.floorId}/area/${reservationData.areaId}/timetable/${reservationData.spaceId}?dates=${dateObj}`
    );
  }, []);

  const handleRequest = async () => {
    clickedReservData.status = "reservado";
    clickedReservData.decisionDate = new Date();
    await axios
      .put(
        `${API_ROUTE}reservation`,
        {
          clickedReservData,
        },
        {
          withCredentials: true,
        }
      )
      .then((res) => {
        window.location.reload();
      });
  };

  const createPopover = (msg) => (
    <Popover id="popover-basic">
      <Popover.Title as="h3">Informação</Popover.Title>
      <Popover.Content>{msg}</Popover.Content>
    </Popover>
  );

  const declineRequest = async () => {
    clickedReservData.decisionDate = new Date();
    clickedReservData.status = "rejeitado";
    clickedReservData.analysisJustification = inputRef.current.value;
    await axios
      .put(
        `${API_ROUTE}reservation`,
        {
          clickedReservData,
        },
        {
          withCredentials: true,
        }
      )
      .then((res) => {
        window.location.reload();
      });
  };

  useEffect(() => {

    let didCancel = false;

    if (!shouldFetch) return;

    const fetchData = async () => {
      if (selectedSchoolCode) {
        await axios
            .get(`${API_ROUTE}reservation?reservationType=${reservationType}&origin=${reservationOrigin}&startDate=${startDate}&endDate=${endDate}&dateType=${dateType}&isAllFromAnalysis=${isAllFromAnalysis}${isSystemMasterManager ? `&schoolCode=${selectedSchoolCode}` : ``}`, {
              withCredentials: true,
            })
            .then((res) => {
              if (didCancel) return;
              setReservationData(res.data.data.reservationData);
              setIsLoading(false);
              setShouldFetch(false);
            })
            .catch(err => {
              console.error("Error fetching reservations:", err);
              setIsLoading(false);
              setShouldFetch(false);
            });
      }
    };

    fetchData();

    return () => {
      didCancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationType, reservationOrigin, startDate, endDate, dateType, isAllFromAnalysis, shouldFetch]);

  const getHoursAndMinutes = (date) => {
    const dateObject = new Date(date);

    const hours = dateObject.getUTCHours();
    const minutes = dateObject.getUTCMinutes();

    return parseFloat(`${hours}.${minutes}`);
  };

  const formatHour = (time) => {
    let formattedTime;
    formattedTime = new Date(time).toUTCString().split(" ")[4].split(":");
    formattedTime.pop();

    return formattedTime[0] + ":" + formattedTime[1];
  };

  const createToasts = (unformatedReservationData) => {
    const tmpArr = React.Children.toArray(
      unformatedReservationData.map((val, i) => {
        if (val.status === "em análise") {
          let reservationConflict = false;
          let yellowReservationConflict = false; // when there is a request to a time already reserved

          const start1 = getHoursAndMinutes(val.timeStart);
          const end1 = getHoursAndMinutes(val.timeEnd);

          // day month and year must be the same to compare
          const date1 = new Date(val.timeStart);
          date1.setHours(0, 0, 0, 0);

          unformatedReservationData.forEach((element, idx) => {
            const date2 = new Date(element.timeStart);
            date2.setHours(0, 0, 0, 0);
            if (
              i !== idx &&
              element.spaceId === val.spaceId &&
              date1.getTime() === date2.getTime()
            ) {
              const start2 = getHoursAndMinutes(element.timeStart);
              const end2 = getHoursAndMinutes(element.timeEnd);

              // check conflicts based on the hours
              if (
                (start1 >= start2 && start1 < end2) ||
                (start2 >= start1 && start2 < end1)
              ) {
                if (element.status === "reservado") {
                  yellowReservationConflict = true;
                } else if (element.status === "em análise") {
                  reservationConflict = true;
                }
              }
            }
          });

          let elapsedTime = Math.abs(Date.now() - new Date(val.requestDate));
          if (elapsedTime / 1000 / 60 / 60 > 1) {
            elapsedTime =
              parseInt(elapsedTime / 1000 / 60 / 60).toString() +
              `${
                parseInt(elapsedTime / 1000 / 60 / 60) === 1
                  ? " hora atrás"
                  : " horas atrás"
              }`;
          } else if (elapsedTime / 1000 / 60 < 1) {
            elapsedTime =
              parseInt(elapsedTime / 1000).toString() + ` segundos atrás`;
          } else {
            elapsedTime =
              parseInt(elapsedTime / 1000 / 60).toString() +
              `${
                parseInt(elapsedTime / 1000 / 60) === 1
                  ? " minuto atrás"
                  : " minutos atrás"
              }`;
          }

          const formattedTimeStart = formatHour(val.timeStart);
          const formattedTimeEnd = formatHour(val.timeEnd);

          return (
            <Toast
              onClose={() => {
                setClickedReservData(val);
                toggleRefuseReservationModal();
              }}
              className={
                yellowReservationConflict
                  ? "notification-toast-yellow"
                  : reservationConflict
                  ? "notification-toast-red"
                  : "notification-toast"
              }
            >
              <Toast.Header className="notification-toast--header">
                <strong className="mr-auto">{val.spaceName}</strong>
                <small>{elapsedTime}</small>
                {yellowReservationConflict ? (
                  <FaEye
                    className="icon-notification"
                    onClick={() => goToTimetable(val)}
                  />
                ) : null}
                <OverlayTrigger
                  trigger="click"
                  rootClose={true}
                  placement="top"
                  overlay={createPopover(
                    yellowReservationConflict
                      ? "Esta reserva não pode ser aceita pois já existe uma reserva aceita neste período."
                      : reservationConflict
                      ? "Esta reserva possui conflito com outra(s) reserva(s)."
                      : "Esta reserva não possui nenhum conflito com outras reservas."
                  )}
                >
                  <FaRegQuestionCircle className="icon-notification" />
                </OverlayTrigger>
                {yellowReservationConflict ? null : (
                  <IoMdCheckmark
                    className="icon-notification icon-check"
                    onClick={() => {
                      setClickedReservData(val);
                      toggleConfirmationModal();
                    }}
                  />
                )}

                <FaPencilAlt
                  className="icon-notification icon-gray"
                  onClick={() => {
                    setClickedReservData(val);
                    toggleEditReservationModal();
                  }}
                />
              </Toast.Header>

              <Toast.Body>
                <strong>Solicitante:</strong> {val.requester}
                <br />
                <strong>Motivo:</strong> {val.reason}
                <br />
                <strong>Descrição:</strong> {val.description}
                <br />
                <strong>Data de Solicitação: </strong>
                {new Date(val.requestDate).toLocaleDateString()} <br />
                <strong>Data: </strong>
                {new Date(val.timeStart).toLocaleDateString()} <br />
                <strong>Período de Início: </strong>
                {formattedTimeStart}
                <br />
                <strong>Período de Fim: </strong> &nbsp;&nbsp;&nbsp;
                {formattedTimeEnd}
                <br />
              </Toast.Body>
            </Toast>
          );
        } else if (reservationType !== "em análise") {
          const formattedTimeStart = formatHour(val.timeStart);
          const formattedTimeEnd = formatHour(val.timeEnd);

          return (
              <Toast
                  show={true}
                  animation={true}
                  className={reservationType !== "reservado" ? "notification-toast-yellow" : "notification-toast"}
              >
                <div className="toast-header">
                  <strong className="mr-auto">{val.spaceName}</strong>
                  {reservationType !== "reservado" && (
                      <IoMdCheckmark
                          className="icon-notification icon-check"
                          onClick={() => {
                            setClickedReservData(val);
                            toggleConfirmationModal();
                          }}
                      />
                  )}
                  <FaPencilAlt
                      className="icon-notification icon-gray"
                      onClick={() => {
                        setClickedReservData(val);
                        toggleEditReservationModal();
                      }}
                  />
                  {reservationType === "reservado" && (
                      <FaTimes
                          className="icon-notification icon-gray"
                          onClick={() => {
                            setClickedReservData(val);
                            toggleRefuseReservationModal();
                          }}
                      />
                  )}
                </div>
                <Toast.Body>
                  <strong>Solicitante:</strong> {val.requester}
                  <br />
                  <strong>Motivo:</strong> {val.reason}
                  <br />
                  <strong>Descrição:</strong> {val.description}
                  <br />
                  {reservationType !== "reservado" && (
                      <>
                        <strong>Justificativa:</strong> {val.analysisJustification}
                        <br />
                      </>
                  )}
                  <strong>Data da Decisão: </strong>
                  {val?.decisionDate ? new Date(val.decisionDate).toLocaleDateString() : "-"}
                  <br />
                  <strong>Data: </strong>
                  {new Date(val.timeStart).toLocaleDateString()} <br />
                  <strong>Período de Início: </strong>
                  {formattedTimeStart}
                  <br />
                  <strong>Período de Fim: </strong> &nbsp;&nbsp;&nbsp;
                  {formattedTimeEnd}
                  <br />
                </Toast.Body>
              </Toast>
          );

        }
      })
    );

    setArrayOfNotifications(tmpArr);
  };

  useEffect(() => {
    if (selectedSort === "byName") {
      // sort by Name
      reservationData.sort((a, b) =>
          a.requester.localeCompare(b.requester, { sensitivity: "base" })
      );
    } else if (selectedSort === "older") {
      // sort by older to newer
      reservationData.sort(
          (a, b) =>
              new Date(new Date(a[dateType])) - new Date(new Date(b[dateType]))
      );
    } else if (selectedSort === "recents") {
      // sort by newer to older
      reservationData.sort(
          (a, b) =>
              new Date(new Date(b[dateType])) - new Date(new Date(a[dateType]))
      );
    }

    const copiedReservationData = [...reservationData].filter(item => item);
    if (isAllFromAnalysis && copiedReservationData.length > 0) {
      copiedReservationData.sort(
          (a, b) => new Date(a[dateType]) - new Date(b[dateType])
      );

      const startDate = new Date(copiedReservationData[0][dateType])
          .toISOString()
          .split('T')[0];

      setShouldFetch(false);
      setStartDate(startDate);
      setEndDate(getFormattedDate(today));
    }

    createToasts(reservationData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationData, selectedSort]);

  const spinn = <Spinner animation="border" />;

  const handleFilter = (evt) => {
    const value = evt.target.value;
    setInputFilterValue(value);
  };

  const handleChangeReservationType = (type) => {
    if (type === "em análise") {
      setIsAllFromAnalysis(true);
    }
    else {
      setIsAllFromAnalysis(false);
      setStartDate(getFormattedDate(eightDaysAgo));
      setEndDate(getFormattedDate(today));
    }
    setReservationType(type);
    setIsLoading(true);
    setShouldFetch(true);
  };

  const handleChangeReservationOrigin = (origin) => {
    setReservationOrigin(origin);
    setIsLoading(true);
    setShouldFetch(true);
  };

  const handleChangeStartDate = (startDate) => {
    setIsAllFromAnalysis(false);
    setStartDate(startDate);
    setShouldFetch(false);
  };

  const handleChangeEndDate = (endDate) => {
    setIsAllFromAnalysis(false);
    setEndDate(endDate);
    setShouldFetch(false);
  };

  const handlePeriodFilter = () => {
    setIsLoading(true);
    setShouldFetch(true);
  };

  const handleDateType = (dateType) => {
    setDateType(dateType);
    setIsLoading(true);
    setShouldFetch(true);
  };

  useEffect(() => {
    const filteredReservation = reservationData.filter((reservation) =>
      reservation[filterType]
        .toLowerCase()
        .includes(inputFilterValue.toLowerCase())
    );
    createToasts(filteredReservation);
  }, [inputFilterValue]);

  return (
    <div className="notification-container">
      <form>
        <h6>Filtrar Por: </h6>
        <input
          type="text"
          value={inputFilterValue}
          placeholder="Espaço / Solicitante"
          onChange={handleFilter}
          className="filter-reservation-input-name"
        />
        <fieldset id="filterGroup">
          <div className="radiobtn">
            <input
              type="radio"
              value="requester"
              name="filterGroup"
              checked={filterType === "requester"}
            />
            <label htmlFor="older" onClick={() => setFilterType("requester")}>
              Solicitante
            </label>
          </div>
          <div className="radiobtn">
            <input
              type="radio"
              value="space"
              name="filterGroup"
              checked={filterType === "spaceName"}
            />
            <label htmlFor="older" onClick={() => setFilterType("spaceName")}>
              Espaço
            </label>
          </div>
        </fieldset>

        <h6>Ordenar: </h6>
        <fieldset id="sortGroup">
          <div className="radiobtn">
            <input
                type="radio"
                value="recents"
                name="sortGroup"
                checked={selectedSort === "recents"}
            />
            <label htmlFor="recents" onClick={() => setSelectedSort("recents")}>
              Mais Recentes
            </label>
          </div>
          <div className="radiobtn">
            <input
              type="radio"
              value="older"
              name="sortGroup"
              checked={selectedSort === "older"}
            />
            <label htmlFor="older" onClick={() => setSelectedSort("older")}>
              Mais Antigos
            </label>
          </div>
          <div className="radiobtn">
            <input
              type="radio"
              value="byName"
              name="sortGroup"
              checked={selectedSort === "byName"}
            />
            <label htmlFor="byDate" onClick={() => setSelectedSort("byName")}>
              Por Nome
            </label>
          </div>
        </fieldset>

        <h6>Mostrar Reservas: </h6>
        <fieldset id="reservationTypeFilter">
          <div className="radiobtn">
            <input
                type="radio"
                value="requester"
                name="reservationTypeFilter"
                checked={reservationType === "em análise"}
            />
            <label htmlFor="older" onClick={() => handleChangeReservationType("em análise")}>
              Em análise
            </label>
          </div>
          <div className="radiobtn">
            <input
                type="radio"
                value="space"
                name="reservationTypeFilter"
                checked={reservationType === "reservado"}
            />
            <label htmlFor="older" onClick={() => handleChangeReservationType("reservado")}>
              Aprovados
            </label>
          </div>
          <div className="radiobtn">
            <input
                type="radio"
                value="space"
                name="reservationTypeFilter"
                checked={reservationType === "rejeitado"}
            />
            <label htmlFor="older" onClick={() => handleChangeReservationType("rejeitado")}>
              Rejeitados
            </label>
          </div>
          <div className="radiobtn">
            <input
                type="radio"
                value="space"
                name="reservationTypeFilter"
                checked={reservationType === "cancelado"}
            />
            <label htmlFor="older" onClick={() => handleChangeReservationType("cancelado")}>
              Cancelados
            </label>
          </div>
        </fieldset>

        <h6>Origem: </h6>
        <fieldset id="reservationOriginFilter">
          <div className="radiobtn">
            <input
                type="radio"
                value="requester"
                name="reservationOriginFilter"
                checked={reservationOrigin === "RESERVAS"}
            />
            <label htmlFor="older" onClick={() => handleChangeReservationOrigin("RESERVAS")}>
              Reservas
            </label>
          </div>
          <div className="radiobtn">
            <input
                type="radio"
                value="space"
                name="reservationOriginFilter"
                checked={reservationOrigin === "SUMÁRIOS"}
            />
            <label htmlFor="older" onClick={() => handleChangeReservationOrigin("SUMÁRIOS")}>
              Sumários
            </label>
          </div>
        </fieldset>

        <h6>Período: </h6>
        <fieldset id="periodFilter" className="mb-3">
          <div className="mb-2">
            <label htmlFor="startDate" className="form-label">De:</label>
            <input
                type="date"
                id="startDate"
                name="startDate"
                value={startDate}
                onChange={(e) => handleChangeStartDate(e.target.value)}
                className="form-control"
            />
          </div>
          <div>
            <label htmlFor="endDate" className="form-label">Até:</label>
            <input
                type="date"
                id="endDate"
                name="endDate"
                value={endDate}
                onChange={(e) => handleChangeEndDate(e.target.value)}
                className="form-control"
            />
          </div>
          <div className="mt-3">
            <button
                className="btn btn-primary w-100"
                type="button"
                onClick={() => handlePeriodFilter()}
            >
              Aplicar Filtro de Período
            </button>
          </div>
        </fieldset>


        <h6>Tipo de Data: </h6>
        <fieldset id="dateTypeFilter">
          <div className="radiobtn">
            <input
                type="radio"
                value="requestDate"
                name="dateTypeFilter"
                checked={dateType === "requestDate"}
            />
            <label htmlFor="requestDate" onClick={() => handleDateType("requestDate")}>
              Data de Solicitação
            </label>
          </div>
          <div className="radiobtn">
            <input
                type="radio"
                value="timeStart"
                name="dateTypeFilter"
                checked={dateType === "timeStart"}
            />
            <label htmlFor="timeStart" onClick={() => handleDateType("timeStart")}>
              Data da Reserva
            </label>
          </div>
        </fieldset>

      </form>

      <div className="notification-grid">
        {
          isLoading ? spinn : arrayOfNotifications.length > 0
              ? arrayOfNotifications
              : "Nenhuma solicitação encontrada. Verifique o tipo e o período selecionado."
        }
      </div>

      <RefuseReservationModal
          isShowing={isShowingRefuseReservationModal}
          toggle={toggleRefuseReservationModal}
          declineRequest={declineRequest}
          inputRef={inputRef}
      />

      <EditReservationModal
          isShowing={isShowingEditReservationModal}
          toggle={toggleEditReservationModal}
          reservationData={clickedReservData}
      />

      <ConfirmationModal
          isShowing={isShowingConfirmationModal}
          toggle={toggleConfirmationModal}
          handleConfirmation={handleRequest}
          titleText={"Aceitar Pedido de Reserva"}
      />
    </div>
  );
};

export default NotificationPage;
